import type { Attachment, Message, Transport, TransportRequest } from '../types';
import { describeHttpError, readLines } from './http';
import { resolveHeaders } from '../utils';

type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } };

function contentFor(message: Message, attachments: Attachment[]): string | ContentPart[] {
  if (attachments.length === 0) return message.content;

  const images = attachments.filter(
    (a) => a.mimeType.startsWith('image/') && (a.url || a.data),
  );
  const others = attachments.filter((a) => !images.includes(a));

  const parts: ContentPart[] = [];
  let text = message.content;
  if (others.length) {
    text += `\n\n[Attached files: ${others.map((a) => a.name).join(', ')}]`;
  }
  parts.push({ type: 'text', text });

  for (const image of images) {
    parts.push({
      type: 'image_url',
      image_url: { url: image.url ?? `data:${image.mimeType};base64,${image.data}` },
    });
  }
  return parts;
}

function buildBody(req: TransportRequest): Record<string, unknown> {
  const { config } = req;
  const limit = config.historyLimit;
  const history = limit > 0 ? req.messages.slice(-1 * (limit + 1), -1) : [];

  const body: Record<string, unknown> = {
    model: 'gpt-4o-mini',
    ...config.body,
    messages: [
      ...history
        .filter((m) => m.status !== 'error' && m.content.trim() !== '')
        .map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: contentFor(req.message, req.attachments) },
    ],
    stream: true,
  };

  return config.transformRequest ? config.transformRequest(body, req) : body;
}

/**
 * OpenAI-compatible `/v1/chat/completions` (OpenAI, Azure, vLLM, Ollama,
 * OpenRouter, LiteLLM, ...). Streams `choices[0].delta.content`.
 *
 * Attachments are sent as `image_url` parts when they are images with a URL or
 * base64 body; other files are listed by name in the text part. Set
 * `attachmentMode: 'upload-endpoint'` or `'base64'` so URLs/data are available.
 */
export const openaiTransport: Transport = async function* (req) {
  const { config } = req;
  const headers = await resolveHeaders(config, {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  });

  const response = await fetch(config.apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(buildBody(req)),
    signal: req.signal,
    credentials: config.credentials,
  });

  if (!response.ok) throw new Error(await describeHttpError(response));

  for await (const line of readLines(response)) {
    if (!line.startsWith('data:')) continue;
    const raw = line.slice(5).trim();
    if (!raw) continue;
    if (raw === '[DONE]') return;

    let chunk: any;
    try {
      chunk = JSON.parse(raw);
    } catch {
      continue;
    }

    if (chunk.error) {
      yield { type: 'error', error: String(chunk.error.message ?? chunk.error) };
      return;
    }

    const delta = chunk.choices?.[0]?.delta?.content;
    if (typeof delta === 'string' && delta) yield { type: 'delta', text: delta };

    const finish = chunk.choices?.[0]?.finish_reason;
    if (finish) yield { type: 'meta', data: { finishReason: finish } };
  }
};
